import React, { useState, useEffect, useContext } from "react";

import {
    Hospital,
    HeartPulse,
    ArrowRight,
    Sparkles,
    ShieldCheck,
    BarChart3,
    XCircle,
    CheckCircle,
    Download,
    Activity,
    Bed,
    ClipboardCheck,
    PlayCircle,
    Stethoscope,
    FileText,
    Users,
    Bot,
    Database,
    Settings,
    LayoutDashboard,
    Siren,
    FileSearch,
    ReceiptText,
    UserRoundSearch,
    Pill,
    MonitorCog,
    Wrench,
    MessageCircle,
    Ambulance,
    Megaphone,
    FlaskConical,
    CalendarClock,
    Brain,
    Boxes,
    LockKeyhole,
} from "lucide-react";

import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { isValidPhoneNumber } from "react-phone-number-input";

import Head from "../../Components/Layout/Head";
import FooterNew from "../../Components/Layout/FooterNew";
import { CountryContext } from "../../CountryContext";
import ApiPath from "../../Components/Common/Apiurl";

import AOS from "aos";
import "aos/dist/aos.css";
import { Helmet } from "react-helmet";
import getHelmetContent from "../../Components/Common/HelmetData";

const hospitalFeatures = [
    {
        id: 1,
        name: "AI Hospital Dashboard & Predictive Analytics",
        purpose: "Executive Leadership / Governance",
        currentEffort:
            "Manual MIS review, cross-department follow-ups and Excel consolidation.",
        afterSystem:
            "Auto dashboards with alerts, forecasting and department-wise visibility.",
        benefit:
            "Faster strategic decisions on patient load, occupancy, revenue, compliance and department performance.",
        details:
            "Integrates with HIS/EMR, billing, admission and department data to generate executive dashboards and predictive insights.",
    },
    {
        id: 2,
        name: "Clinical Performance Analytics & Infection Risk Prediction",
        purpose: "Medical Services Division",
        currentEffort:
            "Manual audit of clinical indicators, infection trends and workload gaps.",
        afterSystem:
            "AI highlights clinical risk patterns, department exceptions and infection-risk indicators.",
        benefit:
            "Improved quality monitoring, better infection control visibility and stronger clinical governance.",
        details:
            "Uses historical patient data, infection records, ward data and clinical parameters to identify early warning patterns.",
    },
    {
        id: 3,
        name: "Smart Bed Allocation, Discharge Prediction & OPD Queue Optimization",
        purpose: "Patient Care Operations - OPD / IPD / ICU / OT",
        currentEffort:
            "Manual bed coordination, discharge follow-ups and queue pressure handling.",
        afterSystem:
            "Priority-based bed recommendations, discharge readiness alerts and queue optimization.",
        benefit:
            "Reduced waiting time, better bed utilization, smoother patient movement and improved patient experience.",
        details:
            "Connects admission, discharge, OPD token, ward and ICU data to recommend operational actions.",
    },
    {
        id: 4,
        name: "ICU Early Warning & Critical Care Response Support",
        purpose: "ICU / Critical Care",
        currentEffort:
            "Manual vitals review and delayed escalation during high-load situations.",
        afterSystem:
            "Risk alerts based on vitals, lab trends, clinical parameters and deterioration signals.",
        benefit:
            "Faster escalation support and improved critical-care responsiveness.",
        details:
            "Uses vitals, reports and ICU monitoring data to identify patients requiring urgent attention.",
    },
    {
        id: 5,
        name: "Patient History Summarization & AI-Assisted Diagnosis Support",
        purpose: "Specialty Clinical Departments",
        currentEffort:
            "Doctors manually scan long histories, previous reports, prescriptions and discharge summaries.",
        afterSystem:
            "Concise clinical summaries and decision-support prompts for doctor review.",
        benefit:
            "Reduced doctor workload, faster case understanding and better consultation preparation.",
        details:
            "Creates structured summaries from EMR, PDFs, lab reports, prescriptions and previous visit notes.",
    },
    {
        id: 6,
        name: "Lab / Radiology Report Summarization & Trend Analysis",
        purpose: "Diagnostics & Ancillary Services",
        currentEffort:
            "Manual reading of long reports and comparison with older values.",
        afterSystem:
            "AI summaries, abnormality highlights and previous-vs-current trend comparison.",
        benefit:
            "Faster report interpretation and early visibility of abnormal trends.",
        details:
            "Reads lab/radiology reports and highlights changes, risk markers and important observations.",
    },
    {
        id: 7,
        name: "Billing Validation, Claim Document Processing & Revenue Forecasting",
        purpose: "Revenue Cycle / Finance",
        currentEffort:
            "Manual bill checks, claim document review and collection tracking.",
        afterSystem:
            "AI-assisted bill validation, claim checklisting and revenue visibility dashboards.",
        benefit:
            "Reduced billing errors, faster claim processing and stronger revenue-cycle discipline.",
        details:
            "Checks bills, insurance documents, discharge summaries and claim requirements for missing or inconsistent information.",
    },
    {
        id: 8,
        name: "AI Resume Screening, Attrition Prediction & HR Analytics",
        purpose: "Human Resources",
        currentEffort: "Manual CV screening, HR reporting and attrition review.",
        afterSystem:
            "Automated shortlist assistance, HR dashboards and attrition-risk insights.",
        benefit:
            "Faster hiring, better workforce planning and data-driven HR decisions.",
        details:
            "Analyzes resumes, staff data, attendance, department load and HR trends.",
    },
    {
        id: 9,
        name: "Medicine Demand Forecasting, Inventory Alerts & Vendor Analytics",
        purpose: "Supply Chain / Pharmacy / Materials",
        currentEffort:
            "Manual stock review, purchase planning, expiry tracking and vendor follow-ups.",
        afterSystem:
            "Predictive stock alerts, demand planning and vendor scorecards.",
        benefit:
            "Lower stockouts, reduced wastage and optimized procurement planning.",
        details:
            "Uses consumption patterns, seasonal demand, department usage and vendor performance data.",
    },
    {
        id: 10,
        name: "Internal IT Chatbot, Log Anomaly Detection & Usage Analytics",
        purpose: "Information Technology",
        currentEffort: "Repeated IT support queries and manual log monitoring.",
        afterSystem:
            "Self-service IT support chatbot, anomaly alerts and usage analytics.",
        benefit:
            "Reduced IT workload and improved system security, uptime and performance monitoring.",
        details:
            "Supports staff queries, monitors logs, detects unusual access and routes IT issues faster.",
    },
    {
        id: 11,
        name: "Predictive Maintenance, Energy Analytics & Safety Alerts",
        purpose: "Facilities & Engineering",
        currentEffort:
            "Manual equipment checks, HVAC review, reactive maintenance and energy monitoring.",
        afterSystem:
            "Maintenance alerts, equipment risk signals and energy consumption analytics.",
        benefit:
            "Lower downtime, better safety compliance and reduced operating waste.",
        details:
            "Tracks equipment health, maintenance schedules, energy patterns and facility alerts.",
    },
    {
        id: 12,
        name: "Patient Chatbot, Smart Scheduling & Feedback Sentiment Analysis",
        purpose: "Patient Experience",
        currentEffort:
            "Manual appointment calls, repetitive FAQs and feedback review.",
        afterSystem:
            "24/7 chatbot, scheduling support and patient feedback sentiment dashboard.",
        benefit:
            "Better patient satisfaction, fewer repetitive calls and reduced waiting friction.",
        details:
            "Handles FAQs, appointment guidance, follow-ups, reminders and feedback classification.",
    },
    {
        id: 13,
        name: "AI CCTV Analytics, Ambulance Routing & Parking Management",
        purpose: "Security & Transport",
        currentEffort:
            "Manual CCTV monitoring, ambulance route coordination and parking management.",
        afterSystem:
            "Crowd alerts, intrusion detection, route suggestions and parking flow insights.",
        benefit:
            "Improved safety, faster emergency response and smoother campus movement.",
        details:
            "Uses CCTV feeds, security data, emergency movement and parking patterns for alerts.",
    },
    {
        id: 14,
        name: "Patient Segmentation, Campaign Analytics & Referral Insights",
        purpose: "Marketing & Outreach",
        currentEffort: "Manual campaign analysis and generic outreach.",
        afterSystem:
            "Segmented outreach, referral analytics and campaign performance tracking.",
        benefit:
            "Better marketing targeting, improved referral visibility and stronger patient acquisition planning.",
        details:
            "Analyzes patient segments, referral sources, campaign response and department-wise demand.",
    },
    {
        id: 15,
        name: "Research Data Extraction, Clinical Trial Matching & CSR Eligibility Analytics",
        purpose: "Academics, Research & Charity",
        currentEffort:
            "Manual data extraction, screening and eligibility evaluation.",
        afterSystem:
            "AI-assisted extraction, matching, scoring and transparency dashboards.",
        benefit:
            "Faster research execution, fair fund allocation and better CSR transparency.",
        details:
            "Extracts structured data from records and supports eligibility matching for research, trials and charity programs.",
    },
];

const featureCategories = [
    "All",
    "Management",
    "Clinical",
    "Operations",
    "Finance",
    "Support",
    "Patient Experience",
];

const getFeatureCategory = (purpose) => {
    const text = purpose.toLowerCase();

    if (
        text.includes("executive") ||
        text.includes("governance") ||
        text.includes("marketing") ||
        text.includes("research") ||
        text.includes("charity")
    ) {
        return "Management";
    }

    if (
        text.includes("clinical") ||
        text.includes("icu") ||
        text.includes("diagnostics") ||
        text.includes("medical")
    ) {
        return "Clinical";
    }

    if (
        text.includes("opd") ||
        text.includes("ipd") ||
        text.includes("patient care") ||
        text.includes("pharmacy") ||
        text.includes("materials") ||
        text.includes("facilities") ||
        text.includes("security") ||
        text.includes("transport")
    ) {
        return "Operations";
    }

    if (
        text.includes("finance") ||
        text.includes("revenue") ||
        text.includes("billing")
    ) {
        return "Finance";
    }

    if (
        text.includes("human resources") ||
        text.includes("information technology")
    ) {
        return "Support";
    }

    if (text.includes("patient experience")) {
        return "Patient Experience";
    }

    return "Operations";
};

const getFeatureIcon = (purpose) => {
    const text = purpose.toLowerCase();

    if (text.includes("executive") || text.includes("governance")) {
        return <LayoutDashboard size={22} />;
    }

    if (text.includes("clinical") || text.includes("medical")) {
        return <Stethoscope size={22} />;
    }

    if (text.includes("icu")) {
        return <Siren size={22} />;
    }

    if (text.includes("diagnostics")) {
        return <FileSearch size={22} />;
    }

    if (text.includes("finance") || text.includes("billing")) {
        return <ReceiptText size={22} />;
    }

    if (text.includes("human resources")) {
        return <UserRoundSearch size={22} />;
    }

    if (text.includes("pharmacy") || text.includes("materials")) {
        return <Pill size={22} />;
    }

    if (text.includes("information technology")) {
        return <MonitorCog size={22} />;
    }

    if (text.includes("facilities")) {
        return <Wrench size={22} />;
    }

    if (text.includes("patient experience")) {
        return <MessageCircle size={22} />;
    }

    if (text.includes("security") || text.includes("transport")) {
        return <Ambulance size={22} />;
    }

    if (text.includes("marketing")) {
        return <Megaphone size={22} />;
    }

    if (text.includes("research")) {
        return <FlaskConical size={22} />;
    }

    return <Hospital size={22} />;
};

const valueAreas = [
    {
        icon: <Bed size={24} />,
        title: "Patient Flow",
        text: "Queue optimization, bed allocation, discharge readiness, OPD scheduling and appointment support.",
    },
    {
        icon: <HeartPulse size={24} />,
        title: "Clinical Support",
        text: "Patient history summaries, diagnostics trends, ICU warnings, infection-risk analytics and doctor-assistive summaries.",
    },
    {
        icon: <ClipboardCheck size={24} />,
        title: "Revenue & Operations",
        text: "Billing validation, claim processing, inventory alerts, HR analytics, IT support and workflow automation.",
    },
    {
        icon: <Hospital size={24} />,
        title: "Management Visibility",
        text: "Hospital dashboards, predictive analytics, facilities alerts, marketing insights and CSR reporting.",
    },
];

const implementationAreas = [
    {
        icon: <Database size={22} />,
        title: "Connect Existing Systems",
        text: "AI can work on top of HIS, EMR, LIS, RIS/PACS, billing, HRMS, ERP and document repositories without replacing current hospital software.",
    },
    {
        icon: <Bot size={22} />,
        title: "Create AI Assistants",
        text: "Role-based assistants can support doctors, nurses, billing teams, front desk, pharmacy, HR, IT and management teams.",
    },
    {
        icon: <ShieldCheck size={22} />,
        title: "Secure Access Control",
        text: "Department-wise access, audit logs, user roles and private deployment options help protect sensitive hospital data.",
    },
    {
        icon: <Settings size={22} />,
        title: "Phased Implementation",
        text: "Hospitals can start with dashboards, chatbot, summaries and billing checks, then expand to predictive analytics and advanced automation.",
    },
];

const consultationPoints = [
    "Improve patient flow",
    "Reduce manual reporting",
    "Automate patient communication",
    "Create secure AI dashboards",
];

const HospitalAI = () => {
    const [selectedCountry, setSelectedCountry] = useState("India");
    const [activeCategory, setActiveCategory] = useState("All");

    const country = useContext(CountryContext);
    const websiteURL = window.location.origin;

    const [formVisible, setFormVisible] = useState(true);
    const [loading, setLoading] = useState(false);
    const [formError, setFormError] = useState("");
    const [phone, setPhone] = useState("");

    useEffect(() => {
        AOS.init({
            duration: 900,
            easing: "ease-out-cubic",
            once: true,
            offset: 80,
        });
    }, []);

    const countryOptions = [
        "Australia",
        "Germany",
        "India",
        "New Zealand",
        "United States",
        "Other",
    ];

    const [formData, setFormData] = useState({
        name: "",
        email: "",
        country: "",
        message: "",
        firmType: "Hospital",
    });

    const [errors, setErrors] = useState({
        name: "",
        email: "",
        phone: "",
        country: "",
        firmType: "",
        message: "",
    });

    useEffect(() => {
        if (countryOptions.includes(country)) {
            setSelectedCountry(country);
        } else {
            setSelectedCountry("India");
        }
    }, [country]);

    const filteredFeatures =
        activeCategory === "All"
            ? hospitalFeatures
            : hospitalFeatures.filter(
                (item) => getFeatureCategory(item.purpose) === activeCategory
            );

    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData({
            ...formData,
            [name]: value,
        });

        setErrors({
            ...errors,
            [name]: "",
        });

        if (name === "country") {
            setSelectedCountry(value);
            setPhone("");

            setErrors((prev) => ({
                ...prev,
                phone: "",
                country: "",
            }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        let newErrors = {
            name: "",
            email: "",
            phone: "",
            country: "",
            firmType: "",
            message: "",
        };

        let isValid = true;

        if (!formData.name.trim()) {
            newErrors.name = "Name is required";
            isValid = false;
        }

        const emailRegex = /^\S+@\S+\.\S+$/;

        if (!formData.email.trim()) {
            newErrors.email = "Email is required";
            isValid = false;
        } else if (!emailRegex.test(formData.email.trim())) {
            newErrors.email = "Invalid email format";
            isValid = false;
        }

        if (!phone) {
            newErrors.phone = "Phone number is required";
            isValid = false;
        } else if (!isValidPhoneNumber(phone)) {
            newErrors.phone = "Please enter a valid phone number";
            isValid = false;
        }

        if (!selectedCountry) {
            newErrors.country = "Please select country";
            isValid = false;
        }

        if (formData.message.trim().length > 300) {
            newErrors.message = "Maximum character limit is 300";
            isValid = false;
        }

        setErrors(newErrors);

        if (!isValid) return;

        const payload = {
            name: formData.name,
            email: formData.email.trim(),
            phone: phone,
            lookingFor: "Healthcare",
            queryPageIndex: "HOSPITAL_AI_PAGE",
            message:
                (formData.message || "") +
                ` IP: ${country} dropdown: ${selectedCountry} | URL: ${websiteURL}`,
        };

        try {
            setLoading(true);
            setFormError("");

            const response = await fetch(
                ApiPath + "/api/enquiryController/tqAiEnquiry",
                {
                    method: "POST",
                    headers: {
                        "Content-Type": "application/json",
                    },
                    body: JSON.stringify(payload),
                }
            );

            if (response.ok) {
                setFormData({
                    name: "",
                    email: "",
                    firmType: "Hospital",
                    message: "",
                });

                if (window.fbq) {
                    window.fbq("track", "Lead");
                }

                setPhone("");
                setFormVisible(false);

                setTimeout(() => {
                    setFormVisible(true);
                    setLoading(false);
                }, 5000);
            } else {
                setFormError("Something went wrong. Please try again.");
                setLoading(false);
            }
        } catch (error) {
            console.error(error);
            setFormError("Something went wrong. Please try again.");
            setLoading(false);
        }
    };

    const getCountryCode = (countryName) => {
        const map = {
            India: "IN",
            "United States": "US",
            Australia: "AU",
            Germany: "DE",
            "New Zealand": "NZ",
        };

        return map[countryName] || "IN";
    };

    const StatCard = ({ icon, label, value, subText }) => (
        <div className="group rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)] lg:p-7">
            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                {icon}
            </div>

            <p className="mt-5 text-xs font-bold uppercase tracking-[0.18em] text-slate-500">
                {label}
            </p>

            <h3 className="mt-3 text-2xl font-black leading-tight text-slate-900 xl:text-2xl">
                {value}
            </h3>

            <p className="mt-3 text-sm leading-7 text-slate-500">{subText}</p>
        </div>
    );

    const pageTitle = "AI Automation For Hospital Operations - ThinkQuotient AI";
    const metaTitle = "AI Automation For Hospital Operations - ThinkQuotient AI";
    const helmetContent = getHelmetContent();

    return (
        <>
            <Helmet>
                <title>{pageTitle}</title>
                <meta name="title" content={metaTitle} />

                {helmetContent.meta.map((metaTag, index) => (
                    <meta key={index} name={metaTag.name} content={metaTag.content} />
                ))}

                {helmetContent.script}

                <script>
                    {`
            (function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
            new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
            j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
            'https://www.googletagmanager.com/gtm.js?id='+i+dl;f.parentNode.insertBefore(j,f);
            })(window,document,'script','dataLayer','GTM-KTNBVH9V');
          `}
                </script>

                <script
                    async
                    src="https://www.googletagmanager.com/gtag/js?id=UA-143016865-1"
                ></script>
                <script>
                    {`
            window.dataLayer = window.dataLayer || []; 
            function gtag() { 
              dataLayer.push(arguments); 
            } 
            gtag("js", new Date()); 
            gtag("config", "UA-143016865-1");
          `}
                </script>

                <script
                    async
                    src="https://www.googletagmanager.com/gtag/js?id=G-BSFPPN5E9D"
                ></script>
                <script>
                    {`
            window.dataLayer = window.dataLayer || [];
            function gtag() { 
              dataLayer.push(arguments); 
            }
            gtag('js', new Date());
            gtag('config', 'G-BSFPPN5E9D');
          `}
                </script>

                <script
                    async
                    src="https://www.googletagmanager.com/gtag/js?id=AW-574047969"
                ></script>
                <script>
                    {`
            window.dataLayer = window.dataLayer || [];
            function gtag() { 
              dataLayer.push(arguments); 
            }
            gtag('js', new Date());
            gtag('config', 'AW-574047969');
          `}
                </script>

                <noscript>{`<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-KTNBVH9V" height="0" width="0" style="display:none;visibility:hidden"></iframe>`}</noscript>

                <script>{`
          !function(f,b,e,v,n,t,s)
          {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
          n.callMethod.apply(n,arguments):n.queue.push(arguments)};
          if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
          n.queue=[];t=b.createElement(e);t.async=!0;
          t.src=v;s=b.getElementsByTagName(e)[0];
          s.parentNode.insertBefore(t,s)}(window, document,'script',
          'https://connect.facebook.net/en_US/fbevents.js');
          fbq('init', '1317328810324214');
          fbq('track', 'PageView');
        `}</script>

                <noscript>{`<img height="1" width="1" style="display:none"
          src="https://www.facebook.com/tr?id=1317328810324214&ev=PageView&noscript=1"
        />`}</noscript>
            </Helmet>

            <Head />

            <div className="relative min-h-screen overflow-hidden bg-slate-50 text-slate-900">
                {/* HERO */}
                {/* HERO */}
                <section
                    className="relative overflow-hidden bg-cover bg-center bg-no-repeat py-10 sm:py-10 lg:py-10"
                    style={{
                        backgroundImage: "url('/assets/images/sl_040220_29550_15.jpg')",
                    }}
                >
                    {/* Dark readability overlay */}
                    <div className="absolute inset-0 bg-slate-950/15"></div>

                    {/* Left side stronger overlay for text */}
                    <div className="absolute inset-0 bg-gradient-to-r from-slate-950/35 via-blue-950/25 to-blue-950/30"></div>

                    {/* Soft glow effects */}
                    <div
                        data-aos="zoom-in"
                        data-aos-delay="100"
                        className="absolute -left-24 top-10 h-96 w-96 rounded-full bg-cyan-400/15 blur-3xl"
                    ></div>

                    <div
                        data-aos="zoom-in"
                        data-aos-delay="250"
                        className="absolute bottom-0 right-0 h-96 w-96 rounded-full bg-blue-500/20 blur-3xl"
                    ></div>

                    <div className="relative z-10 mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="grid grid-cols-1 items-center gap-12 xl:gap-20 lg:grid-cols-12">

                            {/* LEFT CONTENT */}
                            <div
                                className="lg:col-span-7 xl:col-span-7 text-center lg:text-left"
                                data-aos="fade-right"
                            >
                                <div
                                    data-aos="fade-up"
                                    data-aos-delay="100"
                                    className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/10 px-5 py-3 text-sm font-bold text-white backdrop-blur-md"
                                >
                                    <Sparkles size={16} className="text-cyan-300" />
                                    AI Solutions For Hospitals
                                </div>

                                <h1
                                    data-aos="fade-up"
                                    data-aos-delay="200"
                                    className="mt-8 text-4xl font-black leading-[1.02] tracking-[-0.04em] text-white text-2xl sm:text-5xl xl:text-5xl"
                                >
                                    AI Automation
                                    <span className="mt-3 block bg-gradient-to-r from-cyan-300 via-blue-200 to-white bg-clip-text py-2 text-transparent">
                                        For Hospital Operations
                                    </span>
                                </h1>

                                <p
                                    data-aos="fade-up"
                                    data-aos-delay="300"
                                    className="mt-5 max-w-2xl text-base leading-8 text-white/80 sm:text-lg mx-auto lg:mx-0"
                                >
                                    Improve hospital operations with AI-enabled dashboards, clinical summaries,
                                    patient communication automation, billing checks, inventory forecasting,
                                    workflow assistance and management visibility.
                                </p>

                                <div
                                    data-aos="fade-up"
                                    data-aos-delay="400"
                                    className="mt-10 flex flex-col gap-4 sm:flex-row sm:flex-wrap justify-center lg:justify-start"
                                >
                                    <a
                                        href="#contact-form"
                                        className="group inline-flex items-center justify-center gap-2 rounded-2xl bg-white px-8 py-4 font-bold text-slate-950 shadow-xl transition-all duration-300 hover:-translate-y-1 hover:bg-cyan-50"
                                    >
                                        Get AI Consultation
                                        <ArrowRight
                                            size={18}
                                            className="transition-transform duration-300 group-hover:translate-x-1"
                                        />
                                    </a>

                                    <a
                                        href="#demo-video"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold text-white shadow-lg backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                                    >
                                        Watch Demo
                                        <PlayCircle size={18} />
                                    </a>

                                    <a
                                        href="/assets/pdfs/AI-Enabled Hospital Operations Automation.pdf"
                                        download
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/10 px-8 py-4 font-bold text-white shadow-lg backdrop-blur-xl transition-all duration-300 hover:-translate-y-1 hover:bg-white/15"
                                    >
                                        Report
                                        <Download size={18} />
                                    </a>
                                </div>
                            </div>

                            {/* RIGHT IMAGE CARD */}
                            <div
                                className="relative lg:col-span-5 xl:col-span-5"
                                data-aos="fade-left"
                                data-aos-delay="250"
                            >
                                <div className="absolute -inset-4 rounded-[2.8rem] bg-cyan-400/15 blur-2xl"></div>

                                <div className="relative overflow-hidden rounded-[2.5rem] border border-white/20 bg-white/10 p-4 shadow-[0_30px_90px_rgba(0,0,0,0.35)] backdrop-blur-xl">
                                    <img
                                        src="/assets/images/front-view-nurses-team-hospital.jpg"
                                        alt="Hospital AI Automation"
                                        className="h-[360px] sm:h-[430px] w-full rounded-[2rem] object-cover"
                                    />

                                    <div className="absolute inset-x-4 bottom-4 rounded-[1.5rem] border border-white/20 bg-slate-950/45 p-5 text-white backdrop-blur-xl">
                                        <div className="inline-flex items-center gap-2 rounded-full bg-cyan-400/15 px-3 py-1 text-xs font-bold text-cyan-200">
                                            <ShieldCheck size={13} />
                                            Secure AI Layer
                                        </div>

                                        <p className="mt-3 text-base sm:text-lg font-bold">
                                            AI layer for hospital operations, not an HMS replacement.
                                        </p>

                                        <p className="mt-2 text-sm leading-6 text-white/75">
                                            Works with existing systems, departments and workflows.
                                        </p>
                                    </div>
                                </div>
                            </div>

                        </div>
                    </div>
                </section>

                {/* AI FEATURES SECTION */}
                <section className="relative overflow-hidden py-20">
                    <div className="absolute -left-24 top-20 h-72 w-72 rounded-full bg-blue-100/70 blur-3xl"></div>
                    <div className="absolute -right-24 bottom-20 h-72 w-72 rounded-full bg-indigo-100/70 blur-3xl"></div>

                    <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="grid items-start gap-10 lg:grid-cols-12">
                            <div className="lg:col-span-8" data-aos="fade-right">
                                <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-white px-5 py-3 text-sm font-bold text-blue-700 shadow-sm">
                                    <BarChart3 size={16} />
                                    AI Feature Benefits
                                </div>

                                <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                                    Practical AI Modules For Hospital Teams
                                </h2>

                                <p className="mt-6 max-w-3xl text-lg leading-9 text-slate-600">
                                    Explore AI modules that support hospital management, doctors,
                                    nurses, billing teams, pharmacy, diagnostics, HR, IT,
                                    facilities and patient experience teams.
                                </p>
                            </div>

                            <div className="lg:col-span-4" data-aos="fade-left">
                                <div className="rounded-[2rem] border border-blue-100 bg-white p-6 shadow-[0_20px_70px_rgba(15,23,42,0.08)]">
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600">
                                        <ShieldCheck size={26} />
                                    </div>

                                    <h3 className="mt-5 text-xl font-black text-slate-900">
                                        AI Layer, Not HMS Replacement
                                    </h3>

                                    <p className="mt-3 text-sm leading-7 text-slate-600">
                                        The AI layer can work on top of HIS, EMR, LIS, RIS/PACS,
                                        billing, HRMS, ERP and document repositories without
                                        disturbing existing hospital operations.
                                    </p>
                                </div>
                            </div>
                        </div>

                        {/* Category Filters */}
                        <div className="mt-12 flex flex-wrap gap-3" data-aos="fade-up">
                            {featureCategories.map((category) => (
                                <button
                                    key={category}
                                    onClick={() => setActiveCategory(category)}
                                    className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all duration-300 ${activeCategory === category
                                            ? "bg-blue-600 text-white shadow-lg shadow-blue-600/20"
                                            : "border border-slate-200 bg-white text-slate-600 hover:border-blue-100 hover:bg-blue-50 hover:text-blue-700"
                                        }`}
                                >
                                    {category}
                                </button>
                            ))}
                        </div>

                        {/* Feature Cards */}
                        <div className="mt-10 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-3">
                            {filteredFeatures.map((item, index) => (
                                <div
                                    key={item.id}
                                    data-aos="fade-up"
                                    data-aos-delay={(index % 6) * 80}
                                    className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:border-blue-100 hover:shadow-[0_24px_80px_rgba(15,23,42,0.10)]"
                                >
                                    <div className="absolute left-0 top-0 h-1.5 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                                    <div className="flex items-start justify-between gap-4">
                                        <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                                            {getFeatureIcon(item.purpose)}
                                        </div>

                                        <span className="rounded-full bg-slate-50 px-3 py-1 text-xs font-bold text-slate-500">
                                            {getFeatureCategory(item.purpose)}
                                        </span>
                                    </div>

                                    <h3 className="mt-5 text-lg font-black leading-7 text-slate-900">
                                        {item.name}
                                    </h3>

                                    <p className="mt-3 text-sm font-semibold text-blue-700">
                                        {item.purpose}
                                    </p>

                                    <div className="mt-5 space-y-4">
                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                                                Current Challenge
                                            </p>
                                            <p className="mt-2 text-sm leading-7 text-slate-600">
                                                {item.currentEffort}
                                            </p>
                                        </div>

                                        <div>
                                            <p className="text-xs font-bold uppercase tracking-[0.16em] text-slate-400">
                                                What AI Does
                                            </p>
                                            <p className="mt-2 text-sm leading-7 text-slate-600">
                                                {item.afterSystem}
                                            </p>
                                        </div>
                                    </div>

                                    <div className="mt-6 rounded-2xl bg-blue-50 p-4">
                                        <div className="flex gap-3">
                                            <CheckCircle
                                                size={18}
                                                className="mt-0.5 shrink-0 text-blue-600"
                                            />
                                            <p className="text-sm font-semibold leading-7 text-blue-800">
                                                {item.benefit}
                                            </p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>

                        <p className="mt-6 text-xs leading-6 text-slate-500">
                            *The above modules are for solution planning. Final
                            implementation scope depends on hospital systems, APIs, data
                            quality, user roles, security policies and department-wise
                            workflow requirements.
                        </p>
                    </div>
                </section>

                {/* HOSPITAL VALUE AREAS */}
                <section className="bg-white py-20">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700">
                                <Activity size={16} />
                                AI Opportunity Areas
                            </div>

                            <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                                Where AI Creates Hospital Value
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                Start with practical, low-disruption workflows and then expand
                                into advanced predictive intelligence across departments.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
                            {valueAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="absolute left-0 top-0 h-1 w-full bg-gradient-to-r from-blue-600 via-indigo-500 to-cyan-400 opacity-0 transition-opacity duration-300 group-hover:opacity-100"></div>

                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-blue-50 text-blue-600 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                                        {item.icon}
                                    </div>

                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>

                                    <p className="mt-4 text-sm leading-7 text-slate-600">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* IMPLEMENTATION APPROACH */}
                <section className="py-20">
                    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                        <div className="mx-auto max-w-3xl text-center" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 rounded-full border border-blue-100 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700">
                                <FileText size={16} />
                                Implementation Approach
                            </div>

                            <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                                AI Layer On Top Of Existing Hospital Software
                            </h2>

                            <p className="mt-6 text-lg leading-9 text-slate-600">
                                The solution can be deployed in phases without disturbing
                                existing hospital operations.
                            </p>
                        </div>

                        <div className="mt-14 grid grid-cols-1 gap-6 md:grid-cols-2 xl:grid-cols-4">
                            {implementationAreas.map((item, index) => (
                                <div
                                    key={index}
                                    className="group rounded-[2rem] border border-slate-200 bg-white p-7 shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_24px_70px_rgba(15,23,42,0.10)]"
                                    data-aos="fade-up"
                                    data-aos-delay={index * 100}
                                >
                                    <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-700 transition-all duration-300 group-hover:bg-blue-600 group-hover:text-white">
                                        {item.icon}
                                    </div>

                                    <h3 className="mt-6 text-xl font-black text-slate-900">
                                        {item.title}
                                    </h3>

                                    <p className="mt-4 text-sm leading-7 text-slate-600">
                                        {item.text}
                                    </p>
                                </div>
                            ))}
                        </div>

                        <div
                            data-aos="fade-up"
                            data-aos-delay="300"
                            className="mt-12 rounded-[2rem] border border-blue-100 bg-blue-50/70 p-6"
                        >
                            <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
                                <div className="flex gap-4">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                                        <LockKeyhole size={22} />
                                    </div>
                                    <div>
                                        <h3 className="font-black text-slate-900">
                                            Role-Based Access
                                        </h3>
                                        <p className="mt-2 text-sm leading-7 text-slate-600">
                                            Department-wise access control for clinical, billing, HR,
                                            IT and management users.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                                        <Brain size={22} />
                                    </div>
                                    <div>
                                        <h3 className="font-black text-slate-900">
                                            Assistive AI
                                        </h3>
                                        <p className="mt-2 text-sm leading-7 text-slate-600">
                                            AI supports review and decision-making; final decisions
                                            stay with hospital teams.
                                        </p>
                                    </div>
                                </div>

                                <div className="flex gap-4">
                                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-white text-blue-600">
                                        <Boxes size={22} />
                                    </div>
                                    <div>
                                        <h3 className="font-black text-slate-900">
                                            Modular Rollout
                                        </h3>
                                        <p className="mt-2 text-sm leading-7 text-slate-600">
                                            Start with one module and expand gradually across
                                            departments as adoption improves.
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* VIDEO */}
                <section id="demo-video" className="bg-white py-20">
                    <div className="mx-auto max-w-7xl px-0 sm:px-6 lg:px-8 md:px-4">
                        <div className="px-4 text-center" data-aos="fade-up">
                            <div className="inline-flex items-center gap-2 border border-blue-100 bg-blue-50 px-5 py-3 text-sm font-bold text-blue-700 md:rounded-full">
                                <PlayCircle size={16} />
                                AI Hospital Workflow Demo
                            </div>

                            <h2 className="mt-6 text-4xl font-black leading-tight tracking-[-0.04em] text-slate-900 lg:text-5xl">
                                See How AI Can Transform Hospital Operations
                            </h2>

                            <p className="mx-auto mt-6 max-w-3xl text-lg leading-9 text-slate-600">
                                Practical automation workflows for hospitals, clinics and
                                healthcare operations teams.
                            </p>
                        </div>

                        <div
                            className="mt-12 max-w-5xl text-center justify-self-center border border-slate-200 bg-white p-5 shadow-[0_20px_70px_rgba(15,23,42,0.06)] md:rounded-[2.5rem] lg:p-7"
                            data-aos="zoom-in-up"
                            data-aos-delay="150"
                        >
                            <div className="overflow-hidden bg-black md:rounded-[2rem]">
                                <video
                                    className="aspect-video w-full object-contain"
                                    controls
                                    autoPlay
                                    muted
                                    loop
                                    playsInline
                                >
                                    <source src="/assets/videos/Hospital-AI.mp4" type="video/mp4" />
                                </video>
                            </div>
                        </div>
                    </div>
                </section>

                {/* CONTACT FORM SECTION */}
                <section
                    id="contact-form"
                    className="relative overflow-hidden py-20 sm:py-24"
                >
                    <div className="absolute inset-0 bg-gradient-to-br from-blue-50 via-white to-indigo-50"></div>

                    <div className="relative mx-auto max-w-7xl px-0 sm:px-6 lg:px-8 md:px-4">
                        <div className="grid grid-cols-1 items-center gap-10 lg:grid-cols-2 xl:grid-cols-5">
                            <div
                                className="px-6 lg:col-span-1 xl:col-span-3"
                                data-aos="fade-right"
                            >
                                <div className="inline-flex items-center gap-2 rounded-full border border-blue-200 bg-blue-100 px-4 py-2 text-sm font-semibold text-blue-700">
                                    <Sparkles size={16} />
                                    AI Consultation For Hospitals
                                </div>

                                <h2 className="mt-6 text-4xl font-black leading-tight text-slate-900 sm:text-5xl">
                                    Build Secure
                                    <span className="block bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                                        AI For Hospital Operations
                                    </span>
                                </h2>

                                <p className="mt-6 max-w-xl text-lg leading-8 text-slate-600">
                                    Automate patient communication, improve OPD/IPD workflows,
                                    support doctors with summaries, reduce billing errors, improve
                                    inventory planning, and create department-wise hospital
                                    dashboards.
                                </p>

                                <div className="mt-10 grid grid-cols-1 gap-4 sm:grid-cols-2">
                                    {consultationPoints.map((item, index) => (
                                        <div
                                            key={index}
                                            data-aos="fade-up"
                                            data-aos-delay={index * 100}
                                            className="flex items-center gap-3 rounded-2xl border border-slate-200 bg-white px-5 py-4 shadow-sm"
                                        >
                                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-blue-100 text-blue-600">
                                                <CheckCircle size={18} />
                                            </div>

                                            <span className="font-medium text-slate-700">{item}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            <div
                                className="relative lg:col-span-1 xl:col-span-2"
                                data-aos="fade-left"
                                data-aos-delay="100"
                            >
                                <div className="absolute -inset-2 rounded-[2rem] bg-gradient-to-r from-blue-500 to-indigo-600 opacity-20 blur-xl"></div>

                                <div className="relative overflow-hidden border border-white/40 bg-white/90 shadow-[0_25px_80px_rgba(0,0,0,0.08)] backdrop-blur-xl md:rounded-[2rem]">
                                    <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-8 py-8 text-white">
                                        <div className="flex items-center gap-4">
                                            <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/15 backdrop-blur-md">
                                                <Hospital size={28} />
                                            </div>

                                            <div>
                                                <h3 className="text-2xl font-bold">
                                                    Book AI Consultation
                                                </h3>
                                                <p className="mt-1 text-sm text-blue-100">
                                                    Tell us about your hospital automation requirements
                                                </p>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="p-6 sm:p-8">
                                        {!formVisible && (
                                            <div className="mb-6 rounded-2xl border border-blue-200 bg-blue-50 px-5 py-6 text-blue-700">
                                                <div className="flex flex-col items-center text-center">
                                                    <CheckCircle
                                                        size={52}
                                                        className="mb-4 text-blue-600"
                                                    />
                                                    <h3 className="mb-2 text-xl font-bold">
                                                        Thank You!
                                                    </h3>
                                                    <p className="text-sm sm:text-base">
                                                        Our team will contact you shortly.
                                                    </p>
                                                </div>
                                            </div>
                                        )}

                                        {formError && (
                                            <div className="mb-6 rounded-2xl border border-red-200 bg-red-50 px-5 py-6 text-red-700">
                                                <div className="flex flex-col items-center text-center">
                                                    <XCircle
                                                        size={48}
                                                        className="mb-3 text-red-500"
                                                    />
                                                    <h3 className="mb-2 text-lg font-bold">
                                                        Submission Failed
                                                    </h3>
                                                    <p className="text-sm">{formError}</p>
                                                </div>
                                            </div>
                                        )}

                                        {formVisible && (
                                            <form onSubmit={handleSubmit} className="space-y-5">
                                                <div>
                                                    <input
                                                        type="text"
                                                        name="name"
                                                        placeholder="Full Name"
                                                        value={formData.name}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.name
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.name && (
                                                        <p className="mt-2 text-sm text-red-500">
                                                            {errors.name}
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <div
                                                        className={`rounded-2xl border bg-slate-50 px-4 py-4 transition-all duration-300 ${errors.phone
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus-within:border-blue-500 focus-within:bg-white focus-within:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    >
                                                        <PhoneInput
                                                            key={selectedCountry}
                                                            countries={[
                                                                "IN",
                                                                "US",
                                                                "AU",
                                                                "DE",
                                                                "JP",
                                                                "SG",
                                                                "NZ",
                                                            ]}
                                                            defaultCountry={getCountryCode(selectedCountry)}
                                                            international
                                                            value={phone}
                                                            onChange={(value) => {
                                                                setPhone(value || "");
                                                                setErrors({ ...errors, phone: "" });
                                                            }}
                                                            placeholder="Enter phone number"
                                                        />
                                                    </div>
                                                    {errors.phone && (
                                                        <p className="mt-2 text-sm text-red-500">
                                                            {errors.phone}
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <input
                                                        type="email"
                                                        name="email"
                                                        placeholder="Email Address"
                                                        value={formData.email}
                                                        onChange={handleChange}
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.email
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.email && (
                                                        <p className="mt-2 text-sm text-red-500">
                                                            {errors.email}
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <select
                                                        id="country"
                                                        name="country"
                                                        className={`w-full rounded-2xl border bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.country
                                                                ? "border-red-500"
                                                                : "border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                        onChange={handleChange}
                                                        value={selectedCountry}
                                                    >
                                                        {countryOptions.map((countryName) => (
                                                            <option key={countryName} value={countryName}>
                                                                {countryName}
                                                            </option>
                                                        ))}
                                                    </select>
                                                    {errors.country && (
                                                        <p className="mt-2 text-sm text-red-500">
                                                            {errors.country}
                                                        </p>
                                                    )}
                                                </div>

                                                <div>
                                                    <textarea
                                                        rows={3}
                                                        name="message"
                                                        placeholder="Tell us which hospital workflows, departments, patient communication processes, billing activities, or dashboards you want to automate"
                                                        value={formData.message}
                                                        onChange={handleChange}
                                                        className={`w-full resize-none rounded-2xl bg-slate-50 px-5 py-4 outline-none transition-all duration-300 ${errors.message
                                                                ? "border border-red-500"
                                                                : "border border-slate-200 focus:border-blue-500 focus:bg-white focus:ring-4 focus:ring-blue-100"
                                                            }`}
                                                    />
                                                    {errors.message && (
                                                        <p className="mt-2 text-sm text-red-500">
                                                            {errors.message}
                                                        </p>
                                                    )}
                                                </div>

                                                <button
                                                    type="submit"
                                                    disabled={loading}
                                                    className={`inline-flex w-full items-center justify-center gap-2 rounded-2xl py-4 text-lg font-semibold text-white transition-all duration-300 ${loading
                                                            ? "cursor-not-allowed bg-slate-400"
                                                            : "bg-gradient-to-r from-blue-600 to-indigo-600 shadow-xl hover:scale-[1.02]"
                                                        }`}
                                                >
                                                    {loading
                                                        ? "Submitting..."
                                                        : "Request AI Consultation"}
                                                    {!loading && <ArrowRight size={18} />}
                                                </button>
                                            </form>
                                        )}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <FooterNew />
            </div>
        </>
    );
};

export default HospitalAI;